import { useCallback } from 'react';

import { ModalTransition } from '@atlaskit/modal-dialog';

import {
  useAnalytics,
  useAnalyticsContext,
  AnalyticsProvider,
} from 'src/components/settings/analytics';
import { EVENT_SOURCES } from 'src/components/settings/analytics/event-sources';

import {
  useFocusedMergeQueue,
  useFocusedPullRequest,
  useIsMergeQueuePullRequestsModalOpen,
  useMergeQueueActionModal,
} from '../provider';
import { MergeQueueActionModalType } from '../types';

import { MergeQueueActivateModal } from './merge-queue-activate-modal';
import { MergeQueueClearModal } from './merge-queue-clear-modal';
import { MergeQueueDeleteModal } from './merge-queue-delete-modal';
import { PullRequestDequeueModal } from './pull-request-dequeue-modal';
import { PullRequestMoveToTopModal } from './pull-request-move-to-top-modal';

export const MergeQueueActionModal = () => {
  const { actionSubject } = useAnalyticsContext();
  const { getNamespacesWithParent } = useAnalytics();
  const [{ mergeQueueActionModal }, { setMergeQueueActionModal }] =
    useMergeQueueActionModal();
  const [{ focusedMergeQueue }] = useFocusedMergeQueue();
  const [{ focusedPullRequest }, { setFocusedPullRequest }] =
    useFocusedPullRequest();
  const [, { setIsMergeQueuePullRequestsModalOpen }] =
    useIsMergeQueuePullRequestsModalOpen();

  const onCloseMergeQueueActionModal = useCallback(() => {
    setMergeQueueActionModal(null);
  }, [setMergeQueueActionModal]);

  const onClosePullRequestActionModal = useCallback(() => {
    setFocusedPullRequest(undefined);
    setMergeQueueActionModal(null);
    setIsMergeQueuePullRequestsModalOpen(false);
  }, [
    setFocusedPullRequest,
    setMergeQueueActionModal,
    setIsMergeQueuePullRequestsModalOpen,
  ]);

  return (
    <ModalTransition>
      {mergeQueueActionModal === MergeQueueActionModalType.ACTIVATE && (
        <AnalyticsProvider
          data={{
            source: EVENT_SOURCES.MERGE_QUEUE_ACTIVATE_MODAL,
            actionSubject,
            namespaces: getNamespacesWithParent(
              EVENT_SOURCES.MERGE_QUEUE_ACTIVATE_MODAL
            ),
          }}
        >
          <MergeQueueActivateModal
            onClose={onCloseMergeQueueActionModal}
            focusedMergeQueue={focusedMergeQueue}
          />
        </AnalyticsProvider>
      )}
      {mergeQueueActionModal === MergeQueueActionModalType.DELETE && (
        <AnalyticsProvider
          data={{
            source: EVENT_SOURCES.MERGE_QUEUE_DELETE_MODAL,
            actionSubject,
            namespaces: getNamespacesWithParent(
              EVENT_SOURCES.MERGE_QUEUE_DELETE_MODAL
            ),
          }}
        >
          <MergeQueueDeleteModal
            onClose={onCloseMergeQueueActionModal}
            focusedMergeQueue={focusedMergeQueue}
          />
        </AnalyticsProvider>
      )}
      {mergeQueueActionModal === MergeQueueActionModalType.CLEAR && (
        <AnalyticsProvider
          data={{
            source: EVENT_SOURCES.MERGE_QUEUE_CLEAR_MODAL,
            actionSubject,
            namespaces: getNamespacesWithParent(
              EVENT_SOURCES.MERGE_QUEUE_CLEAR_MODAL
            ),
          }}
        >
          <MergeQueueClearModal
            onClose={onCloseMergeQueueActionModal}
            focusedMergeQueue={focusedMergeQueue}
          />
        </AnalyticsProvider>
      )}
      {mergeQueueActionModal === MergeQueueActionModalType.DEQUEUE_PR && (
        <AnalyticsProvider
          data={{
            source: EVENT_SOURCES.MERGE_QUEUE_DEQUEUE_PR_MODAL,
            actionSubject,
            namespaces: getNamespacesWithParent(
              EVENT_SOURCES.MERGE_QUEUE_DEQUEUE_PR_MODAL
            ),
          }}
        >
          <PullRequestDequeueModal
            onClose={onClosePullRequestActionModal}
            focusedPullRequest={focusedPullRequest}
            focusedMergeQueue={focusedMergeQueue}
          />
        </AnalyticsProvider>
      )}
      {mergeQueueActionModal === MergeQueueActionModalType.MOVE_TO_TOP_PR && (
        <AnalyticsProvider
          data={{
            source: EVENT_SOURCES.MERGE_QUEUE_MOVE_TO_TOP_PR_MODAL,
            actionSubject,
            namespaces: getNamespacesWithParent(
              EVENT_SOURCES.MERGE_QUEUE_MOVE_TO_TOP_PR_MODAL
            ),
          }}
        >
          <PullRequestMoveToTopModal
            onClose={onClosePullRequestActionModal}
            focusedPullRequest={focusedPullRequest}
            focusedMergeQueue={focusedMergeQueue}
          />
        </AnalyticsProvider>
      )}
    </ModalTransition>
  );
};
